
import { supabase } from '@/integrations/supabase/client';

// Expose last captured reasoning for Gemini streaming sessions
export let lastGeminiReasoning: string | null = null;

export const isFileCreationQuery = (text: string): boolean => {
  const query = (text || '').toLowerCase();
  return (
    query.includes('pdf') ||
    query.includes('excel') ||
    query.includes('xlsx') ||
    query.includes('csv') ||
    query.includes('sheet') ||
    query.includes('spreadsheet') ||
    query.includes('download') ||
    query.includes('export') ||
    query.includes('save as') ||
    /create\s+(\w+\s+)?file/i.test(query) ||
    /generate\s+(\w+\s+)?file/i.test(query)
  );
};


export const FILE_CREATION_MARKER = '\n\n[FILE_CREATION_INJECTED]';

export const FILE_CREATION_INSTRUCTION = `
[FILE CREATION]
To create a downloadable file, output a \`file-creation\` JSON block:
\`\`\`file-creation
{"type":"pdf"|"excel"|"csv"|"text","filename":"name.ext","title":"Title","content":"..."}
\`\`\`
Rules:
- excel/csv: CSV string with headers+rows
- pdf: Rich A4 HTML with inline CSS, gradients, colors, styled tables, SVG icons, Unsplash images (https://images.unsplash.com/photo-...?w=600&auto=format&fit=crop), callout boxes. All tags closed, no external CSS.
- Give a brief response first, then the code block.
`;

export const enrichPromptForFileCreation = (content: string): string => {
  if (isFileCreationQuery(content)) {
    console.log("[File Creation] Pattern matched. Appending FILE_CREATION_INSTRUCTION.");
    return `${content}${FILE_CREATION_MARKER}${FILE_CREATION_INSTRUCTION}`;
  }
  return content;
};

export const enrichMessages = (messages: any[]): any[] => {
  if (messages && messages.length > 0) {
    const lastMsgIndex = messages.length - 1;
    const lastMsg = messages[lastMsgIndex];
    if (lastMsg && lastMsg.role === 'user' && isFileCreationQuery(lastMsg.content)) {
      const newMessages = [...messages];
      newMessages[lastMsgIndex] = {
        ...lastMsg,
        content: enrichPromptForFileCreation(lastMsg.content)
      };
      return newMessages;
    }
  }
  return messages;
};


// Simulate streaming when provider/function does not support SSE
const simulateStreamingFromText = async (
  text: string,
  onChunk: (chunk: string) => void,
  options?: { baseDelayMs?: number; maxChunkLen?: number }
): Promise<void> => {
  const baseDelay = options?.baseDelayMs ?? 26; // ~40fps default
  const maxChunkLength = options?.maxChunkLen ?? 90;
  if (!text) return;

  // Prefer sentence-wise chunks; fallback to fixed-size
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const chunks: string[] = [];
  if (sentences.length > 1) {
    let buf = '';
    for (const s of sentences) {
      const next = buf ? `${buf} ${s}` : s;
      if (next.length > maxChunkLength && buf) {
        chunks.push(buf);
        buf = s;
      } else {
        buf = next;
      }
    }
    if (buf) chunks.push(buf);
  } else {
    for (let i = 0; i < text.length; i += maxChunkLength) {
      chunks.push(text.slice(i, i + maxChunkLength));
    }
  }

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    onChunk(chunk);
    const delay = Math.min(baseDelay * (1 + chunk.length / 100), 180);
    await new Promise((r) => setTimeout(r, delay));
  }
};

const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU';

const getAuthHeaders = async () => {
  const { data: authData } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authData?.session?.access_token) {
    headers['Authorization'] = `Bearer ${authData.session.access_token}`;
  } else {
    headers['Authorization'] = `Bearer ${ANON_KEY}`;
  }
  return headers;
};

// Helper to get user context
const getUserContext = (userQuery?: string) => {
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // Strip FILE_CREATION_INSTRUCTION from userQuery to prevent double injection
    // (client already injects it in the message; server-side buildUserContextForAI
    // would re-inject it if it detects keywords in userQuery)
    let cleanQuery = userQuery || "";
    const markerIdx = cleanQuery.indexOf(FILE_CREATION_MARKER);
    if (markerIdx !== -1) {
      cleanQuery = cleanQuery.substring(0, markerIdx).trim();
    }
    // Also strip the instruction text itself if marker was missing
    const instrIdx = cleanQuery.indexOf('\n\n[FILE CREATION]');
    if (instrIdx !== -1) {
      cleanQuery = cleanQuery.substring(0, instrIdx).trim();
    }
    return {
      timezone: timeZone,
      userQuery: cleanQuery
    };
  } catch {
    return {};
  }
};

// Helper: Direct Supabase edge function call (no proxy)
const invokeEdgeFunction = async (functionName: string, options: { body: any }) => {
  try {
    // Auto-inject userContext if not present
    const messages = options.body.messages;
    const prompt = options.body.prompt;
    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : (prompt || '');
    const context = getUserContext(userQuery);
    const { userFactsSummary, ...restBody } = options.body;
    const body = {
      ...restBody,
      userContext: options.body.userContext || context
    };

    const { data, error } = await supabase.functions.invoke(functionName, {
      body
    });

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: any) {
    return { data: null, error: err };
  }
}


// Helper function for streaming Solar Pro responses via edge function
const chatWithSolarProEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 0.7,
  maxTokens: number = 20000,
  userFactsSummary?: string
): Promise<string> => {
  const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
  try {
    const headers = await getAuthHeaders();

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/solarpro-chat`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ messages, temperature, max_tokens: maxTokens, stream: true, userContext: getUserContext(userQuery) }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Solar Pro edge error');
    }

    if (!response.body) throw new Error('Response body is null');

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            const chunk = data.choices[0]?.delta?.content || '';
            if (chunk) processChunk(chunk);
          } catch (e) { }
        }
      }
    }

    return fullResponse;
  } catch (error) {
    console.error('Solar Pro streaming error:', error);
    throw error;
  }
};

export const aiChatService = async (
  model: string,
  messages: any[],
  temperature = 0.7,
  onChunk?: (chunk: string) => void,
  onReasoning?: (reasoning: string) => void,
  userFactsSummary?: string
) => {
  // Enrich message content locally if it is a file creation query
  messages = enrichMessages(messages);

  try {
    const m = (model || '').toLowerCase().trim();

    // Normalize aliases
    const isChatGptAlias = (m === 'chat gpt' || m === 'chatgpt' || m === 'gpt');
    const normalized = isChatGptAlias ? 'glm' : m;

    // Handle Gemini
    if (normalized === 'gemini') {
      if (onChunk) {
        const fullResponse = await chatWithGeminiEdgeStream(messages, onChunk, temperature, onReasoning);
        return fullResponse;
      }

      const { data, error } = await invokeEdgeFunction('gemini-chat', {
        body: { messages, temperature, userFactsSummary }
      });

      if (error) throw error;
      // Extract content from Gemini response format
      let content = '';
      if (typeof data === 'string') {
        content = data;
      } else if (data?.response) {
        content = data.response;
      } else if (data?.content) {
        content = data.content;
      } else if (data?.message) {
        content = data.message;
      } else {
        content = data?.choices?.[0]?.message?.content || JSON.stringify(data);
      }
      if (onChunk) {
        await simulateStreamingFromText(content || '', onChunk);
      }
      return content;
    }

    // Handle Google AI (SerpAPI Google AI Mode)
    if (normalized === 'google ai' || normalized === 'googleai') {
      const { data, error } = await invokeEdgeFunction('googleai-chat', {
        body: { messages, temperature, userFactsSummary }
      });

      if (error) throw error;
      const text = ((): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return (data.text || data.response || data.content || data.message || data?.choices?.[0]?.message?.content || '') as string;
      })();

      // Build structured payload for rich renderer in ChatMessage
      const structured = {
        __googleai: true,
        text,
        data: {
          text_blocks: (data as any)?.text_blocks || [],
          inline_images: (data as any)?.inline_images || (data as any)?.images || (data as any)?.images_results || (data as any)?.gallery || [],
          inline_videos: (data as any)?.inline_videos || (data as any)?.videos || (data as any)?.video_results || [],
          quick_results: (data as any)?.quick_results || (data as any)?.answer_box || (data as any)?.infobox || [],
          references: (data as any)?.references || (data as any)?.related_questions || (data as any)?.people_also_ask || [],
          sources: (data as any)?.sources || (data as any)?.organic_results || (data as any)?.results || [],
          shopping_results: ((data as any)?.shopping_results || (data as any)?.shopping || (data as any)?.shopping_results_organic || []).map((item: any) => ({
            ...item,
            link: item.link || item.product_link || item.source_link || item.url,
            thumbnail: item.thumbnail || item.image || item.image_url,
          })),
          knowledge_graph: (data as any)?.knowledge_graph || null,
          local_results: (data as any)?.local_results || (data as any)?.local_map || [],
        },
      };

      const content = JSON.stringify(structured);
      if (onChunk) await simulateStreamingFromText(text || '', onChunk);

      // Return BOTH the raw data and our custom content string
      return { ...data, content, text } as any;
    }

    // Handle DeepSeek models via OpenRouter
    if (normalized === 'deepseek' || normalized === 'deepseek-r1' || normalized === 'deepseek-v3') {
      const modelType = normalized === 'deepseek' ? 'deepseek-r1' : normalized;
      // If streaming requested, use SSE passthrough from Edge Function
      if (onChunk) {
        const fullResponse = await chatWithOpenrouterEdgeStream(messages, onChunk, modelType, temperature, 8192);
        return fullResponse;
      }

      const { data, error } = await invokeEdgeFunction('openrouter-chat', {
        body: { messages, model: modelType, temperature, userFactsSummary }
      });

      if (error) throw error;
      const content = ((): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return (data.response || data.content || data.message || data?.choices?.[0]?.message?.content || JSON.stringify(data)) as string;
      })();
      if (onChunk) {
        await simulateStreamingFromText(content || '', onChunk);
      }
      return content;
    }

    // Handle Claude (fake - using Gemini via OpenRouter)
    if (normalized === 'claude') {
      const { data, error } = await invokeEdgeFunction('openrouter-chat', {
        body: { messages, model: 'claude', temperature, userFactsSummary }
      });

      if (error) throw error;
      const content = ((): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return (data.response || data.content || data.message || data?.choices?.[0]?.message?.content || JSON.stringify(data)) as string;
      })();
      if (onChunk) await simulateStreamingFromText(content || '', onChunk);
      return { ...data, model: 'claude', content } as any;
    }

    // Handle Llama via Groq
    if (normalized === 'llama') {
      const { data, error } = await invokeEdgeFunction('groq-chat', {
        body: { messages, model: 'llama', temperature, userFactsSummary }
      });

      if (error) throw error;
      const content = ((): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return (data.response || data.content || data.message || data?.choices?.[0]?.message?.content || JSON.stringify(data)) as string;
      })();
      if (onChunk) await simulateStreamingFromText(content || '', onChunk);
      return content;
    }

    // Handle GLM (ChatGPT via Supabase function 'chatgpt')
    if (normalized === 'glm') {
      // If caller wants streaming, request non-streaming from edge and simulate locally
      // to avoid SSE plumbing issues with supabase.functions.invoke
      const wantStream = Boolean(onChunk);
      const { data, error } = await invokeEdgeFunction('chatgpt_v2', {
        body: { messages, temperature, stream: wantStream ? false : true, userFactsSummary }
      });

      if (error) throw error;

      // Helper to parse potential SSE-like string payloads that slipped through
      const parseSSEString = (s: string): string => {
        try {
          // Typical lines: "data: {json}" ... "data: [DONE]"
          const lines = s.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
          let text = '';
          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const payload = line.slice(5).trim();
            if (payload === '[DONE]') break;
            try {
              const obj = JSON.parse(payload);
              const delta = obj?.choices?.[0]?.delta?.content;
              const msg = obj?.choices?.[0]?.message?.content;
              if (typeof delta === 'string') text += delta;
              else if (typeof msg === 'string') text += msg;
            } catch { }
          }
          return text || s;
        } catch {
          return s;
        }
      };

      // Prefer structured return if present
      let content = '';
      if (typeof data === 'string') {
        // If edge ever returned a raw SSE or raw string, try to extract text
        content = parseSSEString(data);
      } else if (data && typeof data === 'object') {
        content =
          (data as any).response ||
          (data as any).content ||
          (data as any).message ||
          (data as any)?.choices?.[0]?.message?.content ||
          '';
        // If still empty, avoid "{}" in UI by not JSON.stringifying empty objects
      }

      // Simulate streaming when requested
      if (onChunk) {
        await simulateStreamingFromText(content || '', onChunk);
      }

      return content || '';
    }

    // Handle Qwen
    if (normalized === 'qwen') {
      if (onChunk) {
        const fullResponse = await chatWithQwenEdgeStream(messages, onChunk, temperature, 4096);
        return fullResponse;
      }

      const { data, error } = await invokeEdgeFunction('qwen-chat', {
        body: { messages, temperature, userFactsSummary }
      });

      if (error) throw error;
      // Extract content from Qwen response format
      let content = '';
      if (typeof data === 'string') {
        content = data;
      } else if (data?.response) {
        content = data.response;
      } else if (data?.content) {
        content = data.content;
      } else if (data?.message) {
        content = data.message;
      } else {
        content = data?.choices?.[0]?.message?.content || JSON.stringify(data);
      }
      if (onChunk) await simulateStreamingFromText(content || '', onChunk);
      return content;
    }

    // Handle GPT-OSS model via SambaNova
    if (normalized === 'gpt-oss') {
      if (!onChunk) {
        // Non-streaming via sambanova-chat edge function
        const { data, error } = await invokeEdgeFunction('sambanova-chat', {
          body: { messages, temperature, stream: false, max_tokens: 4096, userFactsSummary }
        });
        if (error) throw error;
        const content = (data?.response || data?.content || data?.message || data?.choices?.[0]?.message?.content || '') as string;
        return content;
      } else {
        // Use the streaming version via SambaNova edge function
        const fullResponse = await chatWithSambanovaEdgeStream(messages, onChunk, temperature, 4096, userFactsSummary);
        return fullResponse;
      }
    }

    // Handle Sambanova AI model via edge function
    if (normalized === 'sambanova') {
      if (!onChunk) {
        const { data, error } = await invokeEdgeFunction('sambanova-chat', {
          body: { messages, temperature, stream: false, userFactsSummary }
        });
        if (error) throw error;
        const content = (data?.response || data?.content || data?.message || data?.choices?.[0]?.message?.content || '') as string;
        if (onChunk) await simulateStreamingFromText(content || '', onChunk);
        return content;
      } else {
        const fullResponse = await chatWithSambanovaEdgeStream(messages, onChunk, temperature, 4096, userFactsSummary);
        return fullResponse;
      }
    }

    // Handle Cerebras AI model via edge function
    if (normalized === 'cerebras') {
      if (!onChunk) {
        const { data, error } = await invokeEdgeFunction('cerebras-chat', {
          body: { messages, temperature, stream: false, userFactsSummary }
        });
        if (error) throw error;
        const content = (data?.response || data?.content || data?.message || data?.choices?.[0]?.message?.content || '') as string;
        if (onChunk) await simulateStreamingFromText(content || '', onChunk);
        return content;
      } else {
        const fullResponse = await chatWithCerebrasEdgeStream(messages, onChunk, temperature, 20000, userFactsSummary);
        return fullResponse;
      }
    }

    // Handle Perplexity (Plexity AI) via edge function
    if (normalized === 'perplexity') {
      const { data, error } = await invokeEdgeFunction('perplexity-chat', {
        body: { messages, temperature, stream: false, userFactsSummary }
      });
      if (error) throw error;
      const content = (data?.response || data?.content || data?.message || data?.choices?.[0]?.message?.content || '') as string;
      if (onChunk) await simulateStreamingFromText(content || '', onChunk);
      // Return the full payload so callers can access citations/search_results
      return data as any;
    }

    // Handle Kimi (Groq)
    if (normalized === 'kimi') {
      if (onChunk) {
        const fullResponse = await chatWithKimiEdgeStream(messages, onChunk, temperature, 8000);
        return fullResponse;
      }

      const { data, error } = await invokeEdgeFunction('kimi-chat', {
        body: { messages, temperature, stream: false, userFactsSummary }
      });
      if (error) throw error;
      const content = ((): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return (data.response || data.content || data.message || (data as any)?.choices?.[0]?.message?.content || '') as string;
      })();
      if (onChunk) await simulateStreamingFromText(content || '', onChunk);
      return content;
    }

    // Handle GPT-5 model via Groq GPT-OSS-120B
    if (normalized === 'gpt5') {
      // If streaming requested, use direct SSE passthrough helper
      if (onChunk) {
        const fullResponse = await chatWithGpt5Stream(messages, onChunk, temperature, 8192);
        return fullResponse;
      }

      // Non-streaming fallback via edge function
      const { data, error } = await invokeEdgeFunction('gpt-5', {
        body: { messages, temperature, stream: false, max_completion_tokens: 8192, userFactsSummary }
      });
      if (error) throw error;
      const content = ((): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return (
          (data as any).response ||
          (data as any).content ||
          (data as any).message ||
          (data as any)?.choices?.[0]?.message?.content ||
          ''
        );
      })();
      if (onChunk) await simulateStreamingFromText(content || '', onChunk);
      return content || '';
    }

    // Handle Upstage Solar Pro
    if (normalized === 'upstage' || normalized === 'solarpro') {
      if (!onChunk) {
        const content = await upstageAiService(messages, undefined, temperature);
        return content;
      } else {
        const content = await upstageAiService(messages, onChunk, temperature);
        return content;
      }
    }

    throw new Error(`Unsupported model: ${model}`);

  } catch (error) {
    console.error('AI Chat Service error:', error);
    throw error;
  }
};

// Helper function for streaming Sambanova responses via edge function
const chatWithSambanovaEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 0.7,
  maxTokens: number = 4096,
  userFactsSummary?: string
): Promise<string> => {
  const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  try {
    const headers = await getAuthHeaders();

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/sambanova-chat`, {
      method: 'POST',
      signal: controller.signal,
      headers,
      body: JSON.stringify({ messages, temperature, max_tokens: maxTokens, stream: true, userContext: getUserContext(userQuery) }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Sambanova edge error');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    // Process chunks immediately for better responsiveness
    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    // Process the stream
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete lines from the buffer
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
            const chunk = data.choices[0]?.delta?.content || '';
            if (chunk) {
              processChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing chunk:', e);
          }
        }
      }
    }

    return fullResponse;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 30 seconds');
    }
    throw error;
  }
};

// Helper function for streaming Cerebras responses via edge function
const chatWithCerebrasEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 0.7,
  maxTokens: number = 20000,
  userFactsSummary?: string
): Promise<string> => {
  const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  try {
    const headers = await getAuthHeaders();

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/cerebras-chat`, {
      method: 'POST',
      signal: controller.signal,
      headers,
      body: JSON.stringify({ messages, temperature, max_tokens: maxTokens, stream: true, userContext: getUserContext(userQuery) }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Cerebras edge error');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    // Process chunks immediately for better responsiveness
    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    // Process the stream
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete lines from the buffer
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
            const chunk = data.choices[0]?.delta?.content || '';
            if (chunk) {
              processChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing chunk:', e);
          }
        }
      }
    }

    return fullResponse;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 30 seconds');
    }
    throw error;
  }
};

// Helper function for streaming OpenRouter-based models (DeepSeek, Claude) via edge function
const chatWithOpenrouterEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  model: string,
  temperature: number = 0.6,
  maxTokens: number = 8192,
  userFactsSummary?: string
): Promise<string> => {
  const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  try {
    const headers = await getAuthHeaders();

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/openrouter-chat`, {
      method: 'POST',
      signal: controller.signal,
      headers,
      body: JSON.stringify({ messages, model, temperature, max_tokens: maxTokens, stream: true, userContext: getUserContext(userQuery) }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'OpenRouter edge error');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            const chunk = data.choices?.[0]?.delta?.content || '';
            if (chunk) {
              processChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing OpenRouter chunk:', e);
          }
        }
      }
    }

    return fullResponse;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 30 seconds');
    }
    throw error;
  }
};

// Helper function for streaming Qwen responses via edge function
const chatWithQwenEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 0.6,
  maxTokens: number = 4096,
  userFactsSummary?: string
): Promise<string> => {
  const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);
  try {
    const headers = await getAuthHeaders();

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/qwen-chat`, {
      method: 'POST',
      signal: controller.signal,
      headers,
      body: JSON.stringify({ messages, temperature, max_completion_tokens: 4096, stream: true, userContext: getUserContext(userQuery) }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Qwen edge error');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            const chunk = data.choices?.[0]?.delta?.content || '';
            if (chunk) {
              processChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing Qwen chunk:', e);
          }
        }
      }
    }

    return fullResponse;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 30 seconds');
    }
    throw error;
  }
};

// Helper function for streaming Kimi responses via edge function
const chatWithKimiEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 0.6,
  maxTokens: number = 8000,
  userFactsSummary?: string
): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);
  try {
    const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
    const { data: authData } = await supabase.auth.getSession();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authData?.session?.access_token) {
      headers['Authorization'] = `Bearer ${authData.session.access_token}`;
    } else {
      headers['Authorization'] = `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWdodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU`;
    }

    const response = await fetch(`${SUPABASE_URL}/functions/v1/kimi-chat`, {
      method: 'POST',
      signal: controller.signal,
      headers,
      body: JSON.stringify({ messages, temperature, max_completion_tokens: maxTokens, stream: true }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Kimi edge error');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            const chunk = data.choices?.[0]?.delta?.content || '';
            if (chunk) {
              processChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing Kimi chunk:', e);
          }
        }
      }
    }

    return fullResponse;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 60 seconds');
    }
    throw error;
  }
};

// Note: chatWithGptOssStream removed - now using chatWithSambanovaEdgeStream instead

// Helper function for streaming GPT-5 responses with optimized performance
const chatWithGpt5Stream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 1,
  maxTokens: number = 8192,
  userFactsSummary?: string
): Promise<string> => {
  try {
    const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
    const { data: authData } = await supabase.auth.getSession();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (authData?.session?.access_token) {
      headers['Authorization'] = `Bearer ${authData.session.access_token}`;
    } else {
      headers['Authorization'] = `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU`;
    }

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/gpt-5`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        messages,
        temperature,
        max_completion_tokens: maxTokens,
        stream: true,
        userContext: getUserContext(userQuery),
        // userFactsSummary removed
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('GPT-5 API Error Response:', errorText);
      throw new Error(`GPT-5 API error: ${response.status} ${response.statusText}`);
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';

    // Process chunks immediately for better responsiveness
    const processChunk = (chunk: string) => {
      fullResponse += chunk;
      onChunk(chunk);
    };

    // Process the stream
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete lines from the buffer
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const data = JSON.parse(line.slice(6));
            const chunk = data.choices[0]?.delta?.content || '';
            if (chunk) {
              processChunk(chunk);
            }
          } catch (e) {
            console.error('Error parsing GPT-5 chunk:', e);
          }
        }
      }
    }

    return fullResponse;
  } catch (error) {
    console.error('GPT-5 streaming error:', error);
    throw error;
  }
};

export const visionChatService = async (prompt: string, imageBase64: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('openrouter-nova', {
      body: {
        messages: [
          { role: 'user', content: String(prompt || 'Analyze this image in detail and as per my query') }
        ],
        image_urls: imageBase64 ? [imageBase64] : []
      }
    });

    if (error) throw error;

    // Normalize OpenRouter response to existing shape
    const content = data?.choices?.[0]?.message?.content ?? data?.message ?? data?.response ?? data?.content ?? '';
    return { content };
  } catch (error) {
    console.error('vision error:', error);
    throw error;
  }
};

export const visionChatServiceMulti = async (prompt: string, imagesBase64: string[]) => {
  try {
    const { data, error } = await supabase.functions.invoke('openrouter-nova', {
      body: {
        messages: [
          { role: 'user', content: String(prompt || 'Analyze these images and describe them') }
        ],
        image_urls: Array.isArray(imagesBase64) ? imagesBase64.filter(Boolean) : []
      }
    });

    if (error) throw error;

    const content = data?.choices?.[0]?.message?.content ?? data?.message ?? data?.response ?? data?.content ?? '';
    return { content };
  } catch (error) {
    console.error('Nova multi-vision error:', error);
    throw error;
  }
};

export const weatherService = async (query: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('weather-agent', {
      body: { query }
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Weather service error:', error);
    throw error;
  }
};

export const newsService = async (category = 'general') => {
  // Use Gemini for news agent responses
  return await aiChatService('gemini', [
    { role: 'system', content: 'You are a news assistant. Provide latest news information.' },
    { role: 'user', content: `Get latest ${category} news` }
  ]);
};

export const currencyService = async (amount: number, from: string, to: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('currency-agent', {
      body: { amount, from, to }
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Currency service error:', error);
    throw error;
  }
};

export const imageGenerationService = async (prompt: string) => {
  try {
    const { data, error } = await supabase.functions.invoke('image-generation', {
      body: { prompt }
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Image generation error:', error);
    throw error;
  }
};

// Upstage Solar Pro AI Service
export const upstageAiService = async (
  messages: any[],
  onChunk?: (chunk: string) => void,
  temperature: number = 0.7,
  maxTokens: number = 4096,
  userFactsSummary?: string
): Promise<string> => {
  // Enrich message content locally if it is a file creation query
  messages = enrichMessages(messages);
  try {
    if (!onChunk) {
      const { data, error } = await supabase.functions.invoke('solarpro-chat', {
        body: { messages, temperature, stream: false }
      });
      if (error) throw error;
      const content = (data?.response || data?.content || data?.message || data?.choices?.[0]?.message?.content || '') as string;
      return content;
    } else {
      const fullResponse = await chatWithSolarProEdgeStream(messages, onChunk, temperature, 2000, userFactsSummary);
      return fullResponse;
    }
  } catch (error) {
    console.error('Error calling Upstage Solar Pro:', error);
    throw new Error(`Failed to get response from Solar Pro: ${error instanceof Error ? error.message : String(error)}`);
  }
};

// Helper function for streaming Gemini responses via edge function
const chatWithGeminiEdgeStream = async (
  messages: any[],
  onChunk: (chunk: string) => void,
  temperature: number = 0.7,
  onReasoning?: (reasoning: string) => void,
  userFactsSummary?: string
): Promise<string> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000);
  try {
    const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';
    const { data: authData } = await supabase.auth.getSession();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authData?.session?.access_token) {
      headers['Authorization'] = `Bearer ${authData.session.access_token}`;
    } else {
      headers['Authorization'] = `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU`;
    }

    const userQuery = messages && messages.length > 0 ? messages[messages.length - 1]?.content : '';
    const response = await fetch(`${SUPABASE_URL}/functions/v1/gemini-chat`, {
      method: 'POST',
      signal: controller.signal,
      headers,
      body: JSON.stringify({ messages, temperature, userContext: getUserContext(userQuery) }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || 'Gemini edge error');
    }

    if (!response.body) {
      throw new Error('Response body is null');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullResponse = '';
    let lastLength = 0;
    // reset reasoning at the start of a new stream
    lastGeminiReasoning = null;

    const processChunk = (chunk: string) => {
      // inference.sh sends full accumulated text in 'response'
      if (chunk.length > lastLength) {
        const delta = chunk.slice(lastLength);
        lastLength = chunk.length;
        fullResponse = chunk;
        onChunk(delta);
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      let newlineIndex;
      while ((newlineIndex = buffer.indexOf('\n')) !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);
        if (line.startsWith('data: ') && !line.endsWith('[DONE]')) {
          try {
            const parsed = JSON.parse(line.slice(6));
            // Handle various inference.sh response formats
            const dataBody = parsed.data || parsed;
            const output = dataBody.output;

            // Check for accumulated text in output.response
            const currentFullText =
              output?.response ||
              output?.text ||
              (typeof dataBody.output === 'string' ? dataBody.output : '') ||
              '';

            // Check for direct token delta
            const tokenDelta = dataBody.token || dataBody.choices?.[0]?.delta?.content || '';

            // Capture reasoning if provided by provider (e.g., inference.sh output.reasoning)
            if (output && typeof output.reasoning === 'string' && output.reasoning) {
              lastGeminiReasoning = output.reasoning;
              try { onReasoning && onReasoning(output.reasoning); } catch { }
            }

            if (currentFullText) {
              processChunk(currentFullText);
            } else if (tokenDelta) {
              fullResponse += tokenDelta;
              lastLength += tokenDelta.length;
              onChunk(tokenDelta);
            }
          } catch (e) {
            // Ignore non-json data lines
          }
        }
      }
    }

    return fullResponse;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timed out after 60 seconds');
    }
    throw error;
  }
};

